import { useEffect, useRef, useState } from 'react';

/**
 * Steps in the login dialog's state machine. `choice` and `email` are the
 * two valid entry points (reachable via URL deep link); `otp` and `welcome`
 * are runtime-only transitions.
 */
export type AuthDialogStep = 'choice' | 'email' | 'otp' | 'welcome';

type UseAuthDialogStepParams = {
  initialStep: Extract<AuthDialogStep, 'choice' | 'email'>;
  isDialogOpen: boolean;
};

interface UseAuthDialogStepResult {
  step: AuthDialogStep;
  setStep: (step: AuthDialogStep) => void;
}

/**
 * Owns the runtime step state for the login dialog. Lifted out of the dialog
 * so `useAuthPostLogin` can set `welcome` directly on first sign-in without
 * reaching into internal state.
 *
 * Resets to `choice` when the dialog closes so a reopen starts fresh. The
 * URL's `step` param is consumed once at mount and not re-read — it's an
 * entry-point hint, not runtime state.
 */
export function useAuthDialogStep({
  initialStep,
  isDialogOpen,
}: UseAuthDialogStepParams): UseAuthDialogStepResult {
  const [step, setStep] = useState<AuthDialogStep>(initialStep);
  const prevOpenRef = useRef(isDialogOpen);

  useEffect(() => {
    if (prevOpenRef.current && !isDialogOpen) {
      setStep('choice');
    }
    prevOpenRef.current = isDialogOpen;
  }, [isDialogOpen]);

  return { step, setStep };
}
